import React from 'react';

import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useGetVisitStatistic } from 'src/features/statistic/api/useGetVisitStatistic';
import DurationPicker from 'src/features/statistic/components/DurationPicker';
import WidgetDurationBreakdown from 'src/features/statistic/components/WidgetDurationBreakdown';
import WidgetTotalVisits from 'src/features/statistic/components/WidgetTotalVisits';
import {
    DataKey,
    TransformedBarItem,
    VisitStatisticHourlyBreakdown,
    VisitStatisticHourlyDurationBreakdown,
    VisitTableStatistics,
} from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import { capacityHourlyBreakdownColorMapping } from 'src/features/statistic/utils/constants';
import { safeFormatMessage } from 'src/utils/intl';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { createColumnHelper } from '@tanstack/react-table';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { siteMessages } from 'src/features/sites/utils/messages';
import { membershipMessages } from 'src/features/membership/utils/messages';
import usePagination from 'src/registry/hooks/usePagination';
import { useListVisits } from 'src/features/statistic/api/useListVisits';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

const getDatetimeToday = () => {
    return {
        from_datetime: dayjs().set('hour', 0).set('minute', 0).format(DATE_FORMAT),
        to_datetime: dayjs().format(DATE_FORMAT),
    };
};

export default function VisitStatistics() {
    const { formatMessage, locale } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const [dateParams, setDateParams] = React.useState(getDatetimeToday);

    const { parkingId } = useParking();
    const { data, isLoading } = useGetVisitStatistic({ ...dateParams, parkingId });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);
    const accessMemberships = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessMembershipTemplate = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.visitStatistics));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const transformPaymentData = React.useCallback(
        ({
            data,
        }: {
            data?: VisitStatisticHourlyBreakdown | VisitStatisticHourlyDurationBreakdown;
        }): {
            dataKeys: DataKey[];
            data: TransformedBarItem[];
        } => {
            if (!data)
                return {
                    dataKeys: [],
                    data: [],
                };

            const dataKeys: DataKey[] = Array.from(
                Object.values(data).reduce((acc, currentValue) => {
                    Object.keys(currentValue).map((key) => acc.add(key));

                    return acc;
                }, new Set<string>()),
            ).map((key) => ({
                title: safeFormatMessage(formatMessage, `${key}s`, key),
                value: key,
            }));

            const newData: TransformedBarItem[] = Object.entries(data).map(([key, item]) => ({
                ...item,
                title: safeFormatMessage(formatMessage, key.replaceAll('_', '-')),
            }));

            return {
                dataKeys,
                data: newData,
            };
        },
        [formatMessage],
    );

    const { data: visitsStats, isLoading: isVisitsStatsLoading } = useListVisits({
        parkingId,
        active_as_of_after: dateParams.from_datetime,
        active_as_of_before: dateParams.to_datetime,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<VisitTableStatistics>();
    const columns = [
        columnHelper.accessor('start_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            meta: {
                title: formatMessage(visitorMessages.enteredAt),
            },
        }),
        columnHelper.accessor('end_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            meta: {
                title: formatMessage(visitorMessages.exitAt),
            },
        }),
        columnHelper.accessor('visitor.main_identifier.value', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        toPath={`/${PATH.VISITORS}/visitor/${ctx.row.original.visitor?.id}`}
                        linkTitle={ctx.row.original.visitor.main_identifier?.value ?? '-'}
                        hasPermissions={accessVisitors}
                    />
                );
            },
            meta: {
                title: formatMessage(visitorMessages.visitor),
            },
        }),
        columnHelper.accessor('site', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                const site = ctx.row.original.site;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        toPath={`/${PATH.ADMINISTRATION}/site/${site.id}`}
                        linkTitle={
                            site?.translations.find((translation) => translation.language === locale)?.name || '-'
                        }
                        hasPermissions={accessSites}
                    />
                );
            },
            meta: {
                title: formatMessage(siteMessages.site),
            },
        }),
        columnHelper.accessor('entry_gateway', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const gatewayName = row.original.entry_gateway?.translations?.find(
                    (gateway) => gateway.language === locale,
                )?.name;
                const gatewayId = row.original.entry_gateway.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/gateway/${gatewayId}`}
                        linkTitle={gatewayName}
                        hasPermissions={accessGateways}
                    />
                );
            },
            meta: {
                title: formatMessage(visitorMessages.entry),
            },
        }),
        columnHelper.accessor('exit_gateway', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const gatewayName = row.original?.exit_gateway?.translations?.find(
                    (gateway) => gateway.language === locale,
                )?.name;
                const gatewayId = row.original?.exit_gateway?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/gateway/${gatewayId}`}
                        linkTitle={gatewayName}
                        hasPermissions={accessGateways}
                    />
                );
            },
            meta: {
                title: formatMessage(visitorMessages.exit),
            },
        }),
        columnHelper.accessor('membership.name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const membershipName = row.original?.membership?.name;
                const membershipId = row.original?.membership?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.MEMBERSHIPS}/all/${membershipId}`}
                        linkTitle={membershipName}
                        hasPermissions={accessMemberships}
                    />
                );
            },
            meta: {
                title: formatMessage(membershipMessages.membership),
            },
        }),
        columnHelper.accessor('membership_type', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const templateName = row.original?.membership_type?.translations?.find(
                    (temp) => temp.language === locale,
                )?.name;
                const templateId = row.original?.membership_type?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/membership-templates/${templateId}`}
                        linkTitle={templateName}
                        hasPermissions={accessMembershipTemplate}
                    />
                );
            },
            meta: {
                title: formatMessage(membershipMessages.template),
            },
        }),
    ];

    return (
        <>
            <div className="flex justify-end">
                <div className="flex gap-4">
                    <DurationPicker dateParams={dateParams} setDateParams={setDateParams} />
                </div>
            </div>
            <div className="flex flex-1 flex-col gap-6">
                <div className="grid grid-cols-1 gap-6 lg:flex xl:flex-wrap">
                    <WidgetTotalVisits isLoading={isLoading} data={data?.total_visits_section} />
                    <WidgetDurationBreakdown
                        className="flex-2"
                        title={formatMessage(statisticMessages.durationBreakdown)}
                        isLoading={isLoading}
                        colorMapping={capacityHourlyBreakdownColorMapping}
                        {...transformPaymentData({ data: data?.duration_breakdown_section })}
                    />
                </div>
                <WidgetDurationBreakdown
                    title={formatMessage(statisticMessages.hourlyVisitsBreakdown)}
                    isLoading={isLoading}
                    colorMapping={capacityHourlyBreakdownColorMapping}
                    {...transformPaymentData({ data: data?.hourly_breakdown_section })}
                />
                <DataTable<VisitTableStatistics, any>
                    isLoading={isVisitsStatsLoading}
                    columns={columns}
                    data={visitsStats}
                    pagination={pagination}
                    semantics={{
                        title: formatMessage(statisticMessages.allVisits),
                        entryName: formatMessage(visitorMessages.visit).toLowerCase(),
                        entryNames: formatMessage(visitorMessages.visits).toLowerCase(),
                    }}
                    components={{
                        controls: DataTableControls,
                        pagination: DataTablePagination,
                    }}
                />
            </div>
        </>
    );
}
